/**
 * A hub chat conversation, in full — readable and printable.
 *
 * WHY IT EXISTS. Josh, 2026-09-01: "we need a way for the transcript to be
 * retained and even printed off." Retention was never the gap — every turn has
 * been stored in hub_chat_messages with its author and timestamp since the
 * table was created. There was simply no way to read one back.
 *
 * ⚠️ THE TEAMS CARD HAS LINKED HERE SINCE IT SHIPPED. Every card carries an
 * "Open the thread" button pointing at /hub/chat/<id>, and this route did not
 * exist, so a staffer who pressed it got nothing. The page was already owed;
 * the transcript request is what surfaced it.
 *
 * ⚠️ IT IS A RECORD, SO IT SHOWS WHAT A RECORD MUST. Full timestamps rather
 * than "2 hours ago", the actual person who answered rather than "Agents
 * Alliance", and the reference. This is guidance about coverage and markets
 * given to a member in writing — the kind of thing that gets read back to
 * somebody a year later, when "who said that, and when" is the entire question.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHero } from '../components/PageHero';
import { BackIcon } from '../components/Icons';
import { ApiError, api, type HubChatMessage } from '../lib/api';

interface Thread {
  id: string;
  status: string;
  claimed_by: string | null;
  agency_name: string | null;
  agent_email: string;
  created_at: string;
  last_message_at: string;
}

/** Full, unambiguous, and the same string on screen and on paper. */
const stamp = (iso: string): string =>
  new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** "cyndy.cates@agentsalliance.biz" reads as Cyndy Cates on a printed page. */
const personName = (author: string | null, fallback: string): string => {
  if (!author) return fallback;
  const local = author.includes('@') ? author.split('@')[0]! : author;
  return local
    .split(/[._-]/)
    .filter(Boolean)
    .map((p) => p[0]!.toUpperCase() + p.slice(1))
    .join(' ');
};

export function HubChatTranscript() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [thread, setThread] = useState<Thread | null>(null);
  const [messages, setMessages] = useState<HubChatMessage[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .hubChatThread(id)
      .then((r) => {
        setThread(r.thread as Thread);
        setMessages(r.messages);
      })
      .catch((e) =>
        setError(
          e instanceof ApiError && e.status === 404
            ? 'That conversation does not exist, or it is not yours to read.'
            : 'Could not load that conversation.',
        ),
      );
  }, [id]);

  if (error) {
    return (
      <div className="wrap">
        <div className="back-link" onClick={() => navigate('/hub')}>
          <BackIcon width={16} height={16} /> Back to the hub
        </div>
        <div className="empty">
          <h3>Not available</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="wrap">
        <p className="supp-note">Loading…</p>
      </div>
    );
  }

  return (
    <div className="wrap tx-wrap">
      {/* Everything in .tx-chrome is hidden when printing — see global.css. */}
      <div className="tx-chrome">
        <div className="back-link" onClick={() => navigate('/hub')}>
          <BackIcon width={16} height={16} /> Back to the hub
        </div>
      </div>

      <PageHero
        title={`Conversation ${thread.id}`}
        sub={
          <>
            {thread.agency_name ? `${thread.agency_name} · ` : ''}
            {thread.agent_email} · started {stamp(thread.created_at)}
            {thread.claimed_by ? ` · answered by ${personName(thread.claimed_by, 'Agents Alliance')}` : ''}
          </>
        }
        action={
          <div className="tx-chrome">
            {/* window.print() rather than a generated PDF: the browser's own
                dialogue already offers "Save as PDF" on every platform, and it
                prints exactly what is on screen — which is the point of a record. */}
            <button className="btn btn-primary" onClick={() => window.print()}>
              Print or save as PDF
            </button>
          </div>
        }
      />

      <div className="tx-sheet">
        {messages.length === 0 ? (
          <p className="supp-note">This conversation has no messages.</p>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`tx-turn${m.sender === 'staff' ? ' is-aas' : ''}`}>
              <div className="tx-who">
                <b>
                  {m.sender === 'staff'
                    ? personName(m.author, 'Agents Alliance')
                    : personName(m.author, thread.agent_email)}
                </b>
                <span>{m.sender === 'staff' ? 'Agents Alliance' : thread.agency_name || 'Member'}</span>
                <time>{stamp(m.at)}</time>
              </div>
              {/* pre-wrap, so a multi-paragraph answer prints as it was written. */}
              <div className="tx-body">{m.body}</div>
            </div>
          ))
        )}
      </div>

      {/*
        Printed only. On paper there is no URL bar and no context, so the page
        has to say what it is and stake out what it is not: appetite and market
        guidance, not a coverage opinion and not a binder.
      */}
      <p className="tx-foot">
        Agents Alliance Services — Commercial Hub conversation {thread.id}. Guidance on markets and
        appetite; not a coverage opinion, and not a commitment to quote. Printed {stamp(new Date().toISOString())}.
      </p>
    </div>
  );
}
